import { useEffect, useRef, useState } from 'react'

import { useDeleteMessage, useEditMessage, useToggleReaction } from '../../hooks/queries'
import type { Message } from '../../api/types'
import { bubbleTime } from '../../lib/format'
import {
  CheckIcon,
  DoubleCheckIcon,
  PencilIcon,
  PinIcon,
  ReplyIcon,
  TrashIcon,
} from '../../components/Icons'
import { Gallery } from './Gallery'
import { VideoPlayer } from './VideoPlayer'
import { Reactions } from './Reactions'

interface Props {
  message: Message
  tripId: number
  withUser?: number
  /** Namen über die Sprechblase setzen (im Antwortverlauf des Absenders). */
  showAuthor: boolean
  /** In einer abgeschlossenen Reise wird nichts mehr geschrieben. */
  canReact: boolean
  /** Beschriftung für eine private Nachricht -- je nachdem, wer sie liest. */
  directLabel?: string
  /**
   * „Darauf antworten" -- ohne diese Funktion gibt es den Knopf nicht.
   *
   * `mediaId` kommt aus der Großansicht eines Fotos: dann bezieht sich die
   * Antwort auf genau dieses Bild und nicht auf den ganzen Beitrag.
   */
  onReply?: (message: Message, mediaId?: number) => void
  /** Zum bezogenen Beitrag springen -- der Tipp aufs Zitat. */
  onJump?: (messageId: number) => void
  /**
   * „Auf die Karte" — aus diesem Beitrag ein Highlight machen.
   *
   * Nur der Reisende bekommt das, und nur an eigenen Rundbriefen. Ohne diese
   * Funktion gibt es den Knopf nicht.
   */
  onAufDieKarte?: (message: Message) => void
}

/** Eine Sprechblase. Eigene Nachrichten rechts, fremde links. */
export function Bubble({
  message,
  tripId,
  withUser,
  showAuthor,
  canReact,
  directLabel,
  onReply,
  onJump,
  onAufDieKarte,
}: Props) {
  const remove = useDeleteMessage(tripId, withUser)
  const edit = useEditMessage(tripId, withUser)
  const react = useToggleReaction(tripId, withUser)

  // Reaktionen auf die Nachricht selbst; die an den Fotos zeigt die Galerie.
  const own = message.reactions.filter((reaction) => reaction.media_id === 0)
  const bilder = message.media.filter((item) => item.kind === 'image')
  const [draft, setDraft] = useState<string | null>(null)
  const field = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (draft !== null) field.current?.focus()
  }, [draft])

  // Wo überhaupt geantwortet werden kann: an einer fremden, nicht gelöschten
  // Sprechblase in einer Reise, die noch läuft -- und nicht, während gerade
  // etwas geändert wird.
  const antwortbar =
    onReply !== undefined && canReact && !message.own && !message.deleted && draft === null

  function save() {
    const text = (draft ?? '').trim()
    // Unverändert oder leer: nichts senden. Eine leere Nachricht lehnt der
    // Server ohnehin ab, und Löschen ist ein eigener Knopf.
    if (text === '' || text === message.body) {
      setDraft(null)
      return
    }
    edit.mutate({ id: message.id, body: text }, { onSuccess: () => setDraft(null) })
  }

  return (
    <div
      className={message.own ? 'bubble-row bubble-row--own' : 'bubble-row'}
      // Das Ziel für den Sprung aus einem Zitat.
      id={`msg-${message.id}`}
    >
      <div className={message.deleted ? 'bubble bubble--deleted' : 'bubble'}>
        {showAuthor && !message.deleted && (
          <span className="bubble__author">{message.author.display_name}</span>
        )}

        {/* Worauf sich das hier bezieht. Steht **über** allem anderen, denn
            es ist der Anfang des Satzes: erst das Zitat, dann die Antwort
            darauf. */}
        {message.reply_to && !message.deleted && (
          <button
            type="button"
            className="zitat"
            onClick={() => onJump?.(message.reply_to!.id)}
            title="Zur bezogenen Nachricht springen"
          >
            {message.reply_to.media_url && (
              <img className="zitat__bild" src={message.reply_to.media_url} alt="" />
            )}
            <span className="zitat__text">
              <span className="zitat__autor">{message.reply_to.author}</span>
              <span className="zitat__auszug">
                {message.reply_to.deleted
                  ? 'Diese Nachricht wurde gelöscht.'
                  : message.reply_to.body !== ''
                    ? message.reply_to.body
                    : 'Foto'}
              </span>
            </span>
          </button>
        )}

        {/* Eine Nachricht an einen Einzelnen muss als solche dastehen. Ohne
            diese Zeile hält der Mitleser sie für einen Rundbrief -- und
            antwortet, als hätten alle mitgelesen. */}
        {message.kind === 'direct' && !message.deleted && (
          <span className="bubble__privat">🔒 {directLabel ?? 'Nur an dich'}</span>
        )}

        <span className="bubble__meta">
          {bubbleTime(message.created_at)}
          {message.edited_at && !message.deleted && (
            <span title="Nachträglich geändert"> · bearbeitet</span>
          )}
          <Haken read={message.read} />

          {/* An fremden Sprechblasen, nicht an eigenen: auf sich selbst zu
              antworten ergibt keinen Sinn, und an der eigenen Blase stünden
              sonst drei Symbole nebeneinander. Dauerhaft sichtbar statt beim
              Darüberfahren -- am Telefon gibt es kein Darüberfahren. */}
          {antwortbar && (
            <button
              type="button"
              className="icon-button"
              style={{ width: 20, height: 20, marginLeft: 4, verticalAlign: -4 }}
              title="Darauf antworten"
              aria-label="Darauf antworten"
              onClick={() => onReply(message)}
            >
              <ReplyIcon size={13} />
            </button>
          )}

          {message.own && !message.deleted && draft === null && (
            <>
              {/* Aus dem Absatz einen Punkt auf der Karte machen. Text und
                  Zeitpunkt gehen mit hinüber; wo es war, entscheidet die
                  Karte -- die Nachricht weiß es nicht. */}
              {onAufDieKarte && (
                <button
                  type="button"
                  className="icon-button"
                  style={{ width: 20, height: 20, marginLeft: 4, verticalAlign: -4 }}
                  title="Auf die Karte"
                  aria-label="Auf die Karte"
                  onClick={() => onAufDieKarte(message)}
                >
                  <PinIcon size={13} />
                </button>
              )}
              <button
                type="button"
                className="icon-button"
                style={{ width: 20, height: 20, marginLeft: 4, verticalAlign: -4 }}
                title="Nachricht ändern"
                aria-label="Nachricht ändern"
                onClick={() => setDraft(message.body)}
              >
                <PencilIcon size={13} />
              </button>
              <button
                type="button"
                className="icon-button"
                style={{ width: 20, height: 20, verticalAlign: -4 }}
                title="Nachricht löschen"
                aria-label="Nachricht löschen"
                onClick={() => {
                  if (confirm('Diese Nachricht löschen?')) remove.mutate(message.id)
                }}
              >
                <TrashIcon size={13} />
              </button>
            </>
          )}
        </span>

        {/* Videos stehen für sich, Bilder in der Galerie. Ein Video in ein
            Raster aus Quadraten zu stecken hiesse, einen Player auf ein
            Vorschaubild zu schrumpfen -- und die Großansicht der Galerie ist
            fürs Blättern durch Fotos gebaut, nicht fürs Abspielen. */}
        {!message.deleted &&
          message.media
            .filter((item) => item.kind === 'video')
            .map((item) => (
              <VideoPlayer
                key={item.id}
                media={item}
                onReply={antwortbar ? () => onReply!(message, item.id) : undefined}
              />
            ))}

        {!message.deleted && bilder.length > 0 && (
          <Gallery
            media={bilder}
            reactions={message.reactions}
            canReact={canReact}
            onReact={(emoji, mediaId) =>
              react.mutate({ messageId: message.id, emoji, mediaId })
            }
            onReply={antwortbar ? (mediaId) => onReply!(message, mediaId) : undefined}
          />
        )}

        {draft !== null ? (
          <div className="stack" style={{ gap: 6 }}>
            <textarea
              ref={field}
              className="bubble__edit"
              rows={Math.min(8, draft.split('\n').length + 1)}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter sendet, Umschalt+Enter macht einen Absatz -- wie im
                // Schreibfeld darunter.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  save()
                }
                if (event.key === 'Escape') setDraft(null)
              }}
            />
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="button"
                style={{ padding: '6px 12px', fontSize: 14 }}
                disabled={edit.isPending}
                onClick={save}
              >
                {edit.isPending ? 'Einen Moment …' : 'Speichern'}
              </button>
              <button
                type="button"
                className="button button--outline"
                style={{ padding: '6px 12px', fontSize: 14 }}
                onClick={() => setDraft(null)}
              >
                Abbrechen
              </button>
            </div>
            {edit.isError && (
              <span style={{ color: 'var(--danger)', fontSize: 13 }}>
                {edit.error instanceof Error ? edit.error.message : 'Das hat nicht geklappt.'}
              </span>
            )}
          </div>
        ) : message.deleted ? (
          'Diese Nachricht wurde gelöscht.'
        ) : (
          message.body
        )}

        {!message.deleted && (canReact || own.length > 0) && (
          <Reactions
            reactions={own}
            busy={react.isPending}
            onPick={(emoji) =>
              canReact && react.mutate({ messageId: message.id, emoji })
            }
          />
        )}
      </div>
    </div>
  )
}

/**
 * Der Haken an einer eigenen Nachricht.
 *
 * **Ein Haken** heißt „liegt bereit", **zwei** heißen „gelesen" — dieselbe
 * Bedeutung wie in der Chatliste, und aus demselben Grund keine dritte Stufe:
 * ein „zugestellt" im Sinne eines Messengers kann es hier nicht geben. Ohne
 * dauerhafte Verbindung weiß der Server nicht, ob ein Gerät die Nachricht
 * schon geholt hat.
 *
 * Geht die Nachricht an mehrere — ein Rundbeitrag an alle Empfänger —, steht
 * die Zahl daneben: *2/5*. Erst wenn alle gelesen haben, wird sie grün. Ohne
 * die Zahl wäre der Haken eine Behauptung über eine Gruppe, in der er für die
 * meisten nicht stimmt.
 *
 * Ist `read` null, steht hier nichts. Wo nichts zugestellt wird, ist jeder
 * Haken eine Behauptung ins Blaue.
 */
function Haken({ read }: { read: { by: number; of: number } | null }) {
  if (read === null) return null

  const alle = read.by >= read.of
  const text =
    read.of === 1
      ? alle
        ? 'Gelesen'
        : 'Liegt bereit, noch nicht gelesen'
      : `Von ${read.by} von ${read.of} gelesen`

  return (
    <span
      className={alle ? 'bubble__haken bubble__haken--gelesen' : 'bubble__haken'}
      title={text}
    >
      {read.by > 0 ? <DoubleCheckIcon size={13} /> : <CheckIcon size={13} />}
      {read.of > 1 && (
        <span className="bubble__haken-zahl">
          {read.by}/{read.of}
        </span>
      )}
      <span className="visually-hidden">{text}</span>
    </span>
  )
}
